<template>
  <div class="bank-update-request-details-container">
    <div class="container-fluid">
      <div class="row">
        <div class="col-sm-12">
          <!-- Loading State -->
          <div v-if="loading" class="card">
            <div class="card-body text-center py-5">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
              <p class="mt-2">Loading request details...</p>
            </div>
          </div>

          <!-- Error State -->
          <div v-else-if="error" class="card">
            <div class="card-body text-center py-5">
              <i class="fa fa-exclamation-triangle fa-3x text-danger mb-3"></i>
              <h5>{{ error }}</h5>
              <button class="btn btn-primary mt-3" @click="goBack">
                <i class="fa fa-arrow-left"></i> Go Back
              </button>
            </div>
          </div>

          <!-- Request Details -->
          <div v-else-if="request">
            <!-- Header Card -->
            <div class="card">
              <div class="card-header pb-0">
                <div class="d-flex justify-content-between align-items-center">
                  <h5>Bank Update Request Details</h5>
                  <div class="d-flex gap-2">
                    <button class="btn btn-sm btn-secondary" @click="goBack">
                      <i class="fa fa-arrow-left"></i> Back
                    </button>
                  </div>
                </div>
              </div>

              <div class="card-body">
                <!-- Status Badge -->
                <div class="mb-4">
                  <span :class="getStatusBadge(request.status).class" style="font-size: 1.1em; padding: 0.5em 1em;">
                    {{ getStatusBadge(request.status).label }}
                  </span>
                </div>

                <!-- Employee Information -->
                <div class="row mb-4">
                  <div class="col-md-12">
                    <h6 class="border-bottom pb-2 mb-3">Employee Information</h6>
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="text-muted">Employee Name</label>
                    <p class="fw-bold">{{ request.employeeId?.employeeName || 'N/A' }}</p>
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="text-muted">Employee Code</label>
                    <p class="fw-bold">{{ request.employeeId?.employeeCode || 'N/A' }}</p>
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="text-muted">Site Name</label>
                    <p>{{ request.employeeId?.siteId?.siteName || 'N/A' }}</p>
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="text-muted">Email</label>
                    <p>{{ request.employeeId?.email || 'N/A' }}</p>
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="text-muted">Phone</label>
                    <p>{{ request.employeeId?.phone || 'N/A' }}</p>
                  </div>
                </div>

                <!-- New Bank Details -->
                <div class="row mb-4">
                  <div class="col-md-12">
                    <h6 class="border-bottom pb-2 mb-3">New Bank Details (Requested)</h6>
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="text-muted">Account Number</label>
                    <p><code>{{ request.accountNumber }}</code></p>
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="text-muted">Bank Name</label>
                    <p class="fw-bold">{{ request.bankName }}</p>
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="text-muted">IFSC Code</label>
                    <p><code>{{ request.ifscCode }}</code></p>
                  </div>
                  <div class="col-md-12 mb-3" v-if="request.bankPassbookUrl">
                    <label class="text-muted">Bank Passbook Document</label>
                    <div>
                      <a :href="request.bankPassbookUrl" target="_blank" class="btn btn-sm btn-outline-primary">
                        <i class="fa fa-file-pdf-o"></i> View Document
                      </a>
                    </div>
                  </div>
                </div>

                <!-- Current Bank Details -->
                <div class="row mb-4" v-if="request.employeeId?.bankDetails">
                  <div class="col-md-12">
                    <h6 class="border-bottom pb-2 mb-3">Current Bank Details</h6>
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="text-muted">Account Number</label>
                    <p><code>{{ request.employeeId.bankDetails.accountNumber || 'N/A' }}</code></p>
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="text-muted">Bank Name</label>
                    <p>{{ request.employeeId.bankDetails.bankName || 'N/A' }}</p>
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="text-muted">IFSC Code</label>
                    <p><code>{{ request.employeeId.bankDetails.ifscCode || 'N/A' }}</code></p>
                  </div>
                </div>

                <!-- Request Information -->
                <div class="row mb-4">
                  <div class="col-md-12">
                    <h6 class="border-bottom pb-2 mb-3">Request Information</h6>
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="text-muted">Requested By</label>
                    <p>{{ request.requestedBy?.employeeName || 'N/A' }} 
                      <span v-if="request.requestedBy?.employeeCode" class="text-muted">({{ request.requestedBy.employeeCode }})</span>
                    </p>
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="text-muted">Requested Date</label>
                    <p>{{ formatDate(request.requestedAt) }}</p>
                  </div>
                  <div class="col-md-12 mb-3" v-if="request.remarks">
                    <label class="text-muted">Remarks</label>
                    <p>{{ request.remarks }}</p>
                  </div>
                </div>

                <!-- Approval/Rejection Information -->
                <div class="row mb-4" v-if="request.status === 'approved' || request.status === 'rejected'">
                  <div class="col-md-12">
                    <h6 class="border-bottom pb-2 mb-3">
                      {{ request.status === 'approved' ? 'Approval' : 'Rejection' }} Information
                    </h6>
                  </div>
                  <div class="col-md-6 mb-3" v-if="request.approvedBy">
                    <label class="text-muted">Approved By</label>
                    <p>{{ request.approvedBy?.employeeName || request.approvedBy }}</p>
                  </div>
                  <div class="col-md-6 mb-3" v-if="request.approvedAt">
                    <label class="text-muted">Approved Date</label>
                    <p>{{ formatDate(request.approvedAt) }}</p>
                  </div>
                  <div class="col-md-6 mb-3" v-if="request.rejectedBy">
                    <label class="text-muted">Rejected By</label>
                    <p>{{ request.rejectedBy?.employeeName || request.rejectedBy }}</p>
                  </div>
                  <div class="col-md-6 mb-3" v-if="request.rejectedAt">
                    <label class="text-muted">Rejected Date</label>
                    <p>{{ formatDate(request.rejectedAt) }}</p>
                  </div>
                  <div class="col-md-12 mb-3" v-if="request.rejectionReason">
                    <label class="text-muted">Rejection Reason</label>
                    <div class="alert alert-danger">
                      {{ request.rejectionReason }}
                    </div>
                  </div>
                </div>

                <!-- Bank History -->
                <div class="row mb-4" v-if="request.oldBankDetails && request.oldBankDetails.length > 0">
                  <div class="col-md-12">
                    <h6 class="border-bottom pb-2 mb-3">Bank Details History</h6>
                  </div>
                  <div class="col-md-12">
                    <div class="table-responsive">
                      <table class="table table-sm table-bordered">
                        <thead>
                          <tr>
                            <th>Account Number</th>
                            <th>Bank Name</th>
                            <th>IFSC Code</th>
                            <th>Changed Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(history, index) in request.oldBankDetails" :key="index">
                            <td><code>{{ history.accountNumber }}</code></td>
                            <td>{{ history.bankName }}</td>
                            <td><code>{{ history.ifscCode }}</code></td>
                            <td>{{ formatDate(history.changedAt) }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                <!-- Action Buttons -->
                <div class="row" v-if="request.status === 'pending'">
                  <div class="col-md-12">
                    <div class="d-flex gap-2">
                      <button class="btn btn-success" @click="approveRequest">
                        <i class="fa fa-check"></i> Approve Request
                      </button>
                      <button class="btn btn-danger" @click="showRejectModal">
                        <i class="fa fa-times"></i> Reject Request
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Reject Modal -->
    <div v-if="showRejectDialog" class="modal fade show" style="display: block; background: rgba(0,0,0,0.5);" tabindex="-1">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Reject Bank Update Request</h5>
            <button type="button" class="btn-close" @click="closeRejectModal"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label">Employee: <strong>{{ request?.employeeId?.employeeName }}</strong></label>
            </div>
            <div class="mb-3">
              <label class="form-label">Rejection Reason <span class="text-danger">*</span></label>
              <textarea 
                v-model="rejectionReason" 
                class="form-control" 
                rows="4" 
                placeholder="Enter the reason for rejection..."
                required
              ></textarea>
              <small class="text-muted">Please provide a clear reason for the employee</small>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeRejectModal">Cancel</button>
            <button 
              type="button" 
              class="btn btn-danger" 
              @click="confirmReject"
              :disabled="!rejectionReason || rejectionReason.trim() === ''"
            >
              <i class="fa fa-times"></i> Reject Request
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import {
  getBankUpdateRequestById,
  approveBankUpdateRequest,
  rejectBankUpdateRequest,
  formatDate,
  getStatusBadge
} from '@/services/bankUpdateRequestService';

export default defineComponent({
  name: 'BankUpdateRequestDetails',
  setup() {
    const router = useRouter();
    const route = useRoute();
    const request = ref<any>(null);
    const loading = ref(false);
    const error = ref('');
    
    // Reject modal
    const showRejectDialog = ref(false);
    const rejectionReason = ref('');

    const requestId = route.params.id as string;

    // Fetch request details
    const fetchRequestDetails = async () => {
      loading.value = true;
      error.value = '';
      try {
        const response = await getBankUpdateRequestById(requestId);
        if (response.data.success) {
          request.value = response.data.data;
        } else {
          error.value = response.data.message || 'Failed to load request details';
        }
      } catch (err: any) {
        console.error('Error fetching request details:', err);
        error.value = err.response?.data?.message || 'Failed to load request details';
      } finally {
        loading.value = false;
      }
    };

    // Approve request
    const approveRequest = async () => {
      if (!confirm(`Are you sure you want to approve this bank update request for ${request.value?.employeeId?.employeeName}?`)) {
        return;
      }

      try {
        const adminId = localStorage.getItem('userId') || localStorage.getItem('adminId');
        if (!adminId) {
          alert('Admin ID not found. Please login again.');
          return;
        }

        const response = await approveBankUpdateRequest(requestId, adminId);
        if (response.data.success) {
          alert('Bank update request approved successfully!');
          fetchRequestDetails(); // Refresh data
        } else {
          alert(response.data.message || 'Failed to approve request');
        }
      } catch (err: any) {
        console.error('Error approving request:', err);
        alert(err.response?.data?.message || 'Failed to approve request');
      }
    };

    // Show reject modal
    const showRejectModal = () => {
      rejectionReason.value = '';
      showRejectDialog.value = true;
    };

    // Close reject modal
    const closeRejectModal = () => {
      showRejectDialog.value = false;
      rejectionReason.value = '';
    };

    // Confirm reject
    const confirmReject = async () => {
      if (!rejectionReason.value || rejectionReason.value.trim() === '') {
        alert('Please enter a rejection reason');
        return;
      }

      try {
        const adminId = localStorage.getItem('userId') || localStorage.getItem('adminId');
        if (!adminId) {
          alert('Admin ID not found. Please login again.');
          return;
        }

        const response = await rejectBankUpdateRequest(
          requestId,
          adminId,
          rejectionReason.value
        );

        if (response.data.success) {
          alert('Bank update request rejected successfully!');
          closeRejectModal();
          fetchRequestDetails(); // Refresh data
        } else {
          alert(response.data.message || 'Failed to reject request');
        }
      } catch (err: any) {
        console.error('Error rejecting request:', err);
        alert(err.response?.data?.message || 'Failed to reject request');
      }
    };

    // Go back
    const goBack = () => {
      router.push('/bank-update-requests/list');
    };

    // Initialize
    onMounted(() => {
      fetchRequestDetails();
    });

    return {
      request,
      loading,
      error,
      showRejectDialog,
      rejectionReason,
      fetchRequestDetails,
      approveRequest,
      showRejectModal,
      closeRejectModal,
      confirmReject,
      goBack,
      formatDate,
      getStatusBadge
    };
  }
});
</script>

<style scoped>
.bank-update-request-details-container {
  padding: 20px 0;
}

code {
  background-color: #f8f9fa;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 0.9em;
}

label.text-muted {
  font-size: 0.85em;
  font-weight: 600;
  text-transform: uppercase;
  margin-bottom: 0.25rem;
}

.modal.show {
  display: block;
}

.badge {
  padding: 0.5em 1em;
}
</style>
